import Component from '@glimmer/component';
import { service } from '@ember/service';
import { VStack, Section } from 'tio-ui/components/layout';
import PageHero from 'tio-ui/components/page-hero';
import FilterTabs from 'tio-ui/components/filter-tabs';
import type { FilterTab } from 'tio-ui/components/filter-tabs';
import type { ComponentLike } from '@glint/template';
import type RouterService from '@ember/routing/router-service';

export interface TabbedPageTab extends FilterTab {
  /** Fully qualified Ember route name the tab owns; also how the active tab is recognised. */
  route: string;
}

export interface TabbedPageSignature {
  Args: {
    /** Decorative Phosphor icon shown beside the hero title. */
    icon?: ComponentLike<{ Args: { size: number }; Element: SVGElement }>;
    /** Translated hero title; renders as the page's only h1. */
    title: string;
    /** Translated one-line hero subtitle. */
    subtitle?: string;
    /** Decorative Phosphor icon shown beside the section heading. */
    sectionIcon?: ComponentLike<{ Args: { size: number }; Element: SVGElement }>;
    /** Translated heading for the section that wraps the tab strip. */
    sectionTitle: string;
    /**
     * Tab strip in display order. The first entry is the fallback when no route
     * matches, so it should be the parent's index route.
     */
    tabs: TabbedPageTab[];
  };
  Blocks: {
    /** Tab content — a route template passes `{{outlet}}` here. */
    default: [];
  };
  Element: HTMLDivElement;
}

/**
 * Landing-page shell for a product area whose content is split across sibling
 * tab routes: hero, section-wrapped tab strip, then the active tab's content.
 *
 * Selecting a tab transitions to that tab's route and scrolls the window back
 * to the top, so a tall leaf does not leave the next tab scrolled past its own
 * heading.
 */
export default class TabbedPage extends Component<TabbedPageSignature> {
  @service declare router: RouterService;

  /**
   * Id of the tab owning the current route — an exact match, or the longest
   * matching ancestor when the leaf has children of its own, so `area.detail`
   * wins over `area` for `area.detail.child`. Falls back to the first tab while
   * the parent route itself is active.
   */
  get activeTabId() {
    const current = this.router.currentRouteName ?? '';
    const matches = this.args.tabs.filter(
      (tab) => current === tab.route || current.startsWith(`${tab.route}.`)
    );
    const nearest = matches.sort((a, b) => b.route.length - a.route.length)[0];
    return (nearest ?? this.args.tabs[0])?.id;
  }

  selectTab = (id: string) => {
    const tab = this.args.tabs.find((candidate) => candidate.id === id);
    if (!tab) return;

    this.router.transitionTo(tab.route);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  <template>
    <VStack class="gap-8" ...attributes>
      <PageHero
        @icon={{@icon}}
        @title={{@title}}
        @subtitle={{@subtitle}}
        @gradient="brand-soft"
        class="tio-elevation-card-raised"
      />

      <Section @headerClass="tio-row-tight">
        <:header>
          {{#if @sectionIcon}}
            <@sectionIcon @size={{24}} class="tio-icon-default tio-fg-brand" />
          {{/if}}
          {{@sectionTitle}}
        </:header>
        <:body>
          <FilterTabs
            data-test-landing-tabs
            @tabs={{@tabs}}
            @activeId={{this.activeTabId}}
            @onSelect={{this.selectTab}}
            @showCounts={{false}}
            @selectedStateAttribute="aria-pressed"
            class="mb-6"
          />
          {{yield}}
        </:body>
      </Section>
    </VStack>
  </template>
}
